<template>
  <Bar
    ref="chartRef"
    :data="chartData"
    :options="chartOptions"
    :redraw="true"
    style="max-height: 400px;"
  />
</template>


<script setup>
import { ref, watch, onMounted } from 'vue'
import axios from '@/services/axios'
import { Bar } from 'vue-chartjs'
import {
  Chart, BarElement, CategoryScale, LinearScale,
  Title, Tooltip, Legend
} from 'chart.js'

Chart.register(
  BarElement, CategoryScale, LinearScale,
  Title, Tooltip, Legend
)

const props = defineProps({
  endpoint: { type: String, default: '/dashboard/receitas-receber-mensal' },
  dataInicio: { type: String, required: true },
  dataFim:    { type: String, required: true }
})

const chartData = ref({ labels: [], datasets: [] })

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  // ← define aqui como o tooltip se comporta
  interaction: {
    mode: 'index',
    intersect: false
  },
  plugins: {
    title: {
      display: true,
      text: 'Receitas a Receber por Mês',
      align: 'start'
    },
    legend: { display: false },
    tooltip: {
      // callbacks apenas para formatação, sem mode/intersect aqui
      callbacks: {
        // cada segmento formatado como R$
        label(ctx) {
          const val = ctx.parsed.y
          return `${ctx.dataset.label}: ${val.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2
          })}`
        },
        // total do stack
        footer(ctxs) {
          const chart = ctxs[0].chart
          const idx   = ctxs[0].dataIndex
          const total = chart.data.datasets
            .reduce((sum, ds) => sum + ds.data[idx], 0)
          return `Total: ${total.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2
          })}`
        }
      },
      footerFont: { weight: 'bold' }
    }
  },
  scales: {
    x: {
      stacked: true
    },
    y: {
      stacked: true,
      beginAtZero: true,
      ticks: {
        callback(value) {
          return value.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
          })
        }
      }
    }
  }
}

const chartRef = ref(null)

const monthMap = {
  Jan: 'Jan', Feb: 'Fev', Mar: 'Mar', Apr: 'Abr',
  May: 'Mai', Jun: 'Jun', Jul: 'Jul', Aug: 'Ago',
  Sep: 'Set', Oct: 'Out', Nov: 'Nov', Dec: 'Dez'
}

async function fetchReceitas() {
  try {
    const { data } = await axios.get(
      props.endpoint,
      { params: { dataInicio: props.dataInicio, dataFim: props.dataFim } }
    )

    // traduz labels
    const labelsPT = data.labels.map(lbl => {
      const [mon, year] = lbl.split('/')
      return `${monthMap[mon]}/${year}`
    })

    chartData.value = {
      labels: labelsPT,
      datasets: [
        { label: 'Matrículas',          data: data.series.matriculas,          backgroundColor: '#4A90E2' },
        { label: 'Mensalidades',        data: data.series.mensalidades,        backgroundColor: '#1ABC9C' },
        { label: 'Aulas Avulsas',       data: data.series.aulasAvulsas,        backgroundColor: '#F5A623' },
        { label: 'Aulas Experimentais', data: data.series.aulasExperimentais,  backgroundColor: '#E74C3C' },
        { label: 'Vendas da Loja',      data: data.series.vendasLoja,          backgroundColor: '#9B59B6' },
        { label: 'Receitas Gerais',     data: data.series.receitasGerais,      backgroundColor: '#34495E' }
      ]
    }
  } catch (e) {
    console.error('Erro ao carregar receitas a receber mensal:', e)
  }
}

onMounted(fetchReceitas)
watch(() => [props.dataInicio, props.dataFim], fetchReceitas)
</script>

